import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
// contact-support-request: emails now go out from the backend (/api/contact); EmailJS disabled.
// import emailjs from '@emailjs/browser';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getApiBaseUrl, getOrCreateAnonymousId } from '../utils/chatIdentity';

// Whether to run reCAPTCHA normally comes from the backend (GET /api/captcha-config), so the
// frontend follows the server's actual settings: reCAPTCHA runs only when the server has BOTH
// keys, and with either missing the form has no human-check at all. This build-time key is
// only the fallback for when that endpoint is unavailable (an older backend, or a network
// error): it is baked into the bundle, so it must be in the build environment and the app
// rebuilt after it changes.
const RECAPTCHA_SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();
const BUILD_TIME_CAPTCHA = RECAPTCHA_SITE_KEY
    ? { mode: 'recaptcha', siteKey: RECAPTCHA_SITE_KEY }
    : { mode: 'none', siteKey: '' };
const CAPTCHA_CONFIG_TIMEOUT_MS = 4000;

// phone is stored as full digits including dial code e.g. "918297997929"
const getContactPhoneError = (phone, country) => {
    const dialCode = country?.dialCode || '';
    if (!phone || phone === dialCode) return ''; // optional - empty is fine
    const nationalDigits = phone.startsWith(dialCode) ? phone.slice(dialCode.length) : phone;
    if (!nationalDigits) return '';
    if (phone.length < 7 || phone.length > 15) return 'Enter a valid phone number.';
    return '';
};

const ContactPageInner = ({ captcha }) => {
    const navigate = useNavigate();
    const recaptchaOn = captcha.mode === 'recaptcha';
    const { executeRecaptcha } = useGoogleReCaptcha();
    const formRef = useRef(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });
    // full phone value as returned by react-phone-input-2 (dial code + national digits, no +)
    const [phone, setPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState({ dialCode: '1', countryCode: 'us' });
    const [phoneError, setPhoneError] = useState('');
    // PhoneInput only re-guesses the flag/format from `value` on its *initial* mount — a later
    // prop update (e.g. prefilling from the account fetch below) keeps whatever country was
    // already selected and mis-formats the digits under that country's mask. Bumping this key
    // remounts the widget so the account's phone is treated as an initial value and guessed correctly.
    const [phoneKey, setPhoneKey] = useState(0);

    // Set when the field was prefilled from a signed-in visitor's account (auth/me). The
    // fields stay ordinary and editable — this only stops "send another message" from
    // wiping a value the visitor never typed and would have to hunt for again.
    const [emailFromAccount, setEmailFromAccount] = useState(false);
    const [phoneFromAccount, setPhoneFromAccount] = useState(false);

    const [status, setStatus] = useState({
        submitting: false,
        success: false,
        error: null,
        // Set from the /api/contact response: the opened support request and where its
        // confirmation was emailed (null when the visitor gave no email).
        supportRequestNumber: null,
        confirmationEmail: null
    });

    // This page renders under the marketing Layout, which holds no auth state, and the
    // session lives in an httpOnly cookie the browser cannot read — so ask the server.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(`${getApiBaseUrl()}/auth/me`, { credentials: 'include' });
                if (!response.ok) return;
                const data = await response.json();
                if (cancelled || !data?.authenticated) return;

                // Each field fills independently: an email-only account reports phone as
                // "", and a phone-only account reports email as "". Gating one on the
                // other would leave half the users retyping what we already know.
                if (data.email) {
                    setFormData((prev) => ({ ...prev, email: data.email }));
                    setEmailFromAccount(true);
                }
                if (data.phone) {
                    setPhone(data.phone);
                    setPhoneFromAccount(true);
                    setPhoneKey((k) => k + 1);
                }
            } catch {
                // Anonymous visitor, or /auth/me unreachable — leave the form blank.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value, countryData) => {
        setPhone(value);
        setPhoneCountry(countryData);
        if (phoneError) setPhoneError('');
    };

    const handlePhoneBlur = () => {
        setPhoneError(getContactPhoneError(phone, phoneCountry));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const err = getContactPhoneError(phone, phoneCountry);
        if (err) {
            setPhoneError(err);
            return;
        }
        setPhoneError('');

        const normalizedPhone = phone ? `+${phone}` : '';

        let token = '';
        if (recaptchaOn) {
            // v3 loads Google's script asynchronously, so executeRecaptcha is briefly
            // undefined right after mount. Here retrying a moment later genuinely works —
            // unlike the no-key case, where it would never become defined at all.
            if (!executeRecaptcha) {
                setStatus({ submitting: false, success: false, error: 'reCAPTCHA not yet available. Please wait a moment.' });
                return;
            }
        }

        setStatus({ submitting: true, success: false, error: null });

        try {
            if (recaptchaOn) {
                token = await executeRecaptcha('contact_form');
            }

            const API_BASE_URL = getApiBaseUrl();
            const response = await fetch(`${API_BASE_URL}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    first_name: formData.firstName || null,
                    last_name: formData.lastName || null,
                    email: formData.email || null,
                    phone: normalizedPhone || null,
                    message: formData.message,
                    captcha_token: token,
                    // Anonymous per-browser id (chatIdentity.js) — the only "from" we can record
                    // when the visitor submits a question without any name/phone/email.
                    web_session_id: getOrCreateAnonymousId()
                })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Failed to save contact submission.');
            }
            const result = await response.json().catch(() => ({}));

            // contact-support-request: the backend now opens the support request and emails
            // support@carfido.com plus the visitor, so the browser-side EmailJS sends would
            // only duplicate them.
            // const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
            // const TEMPLATE_USER = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_USER;
            // const TEMPLATE_TEAM = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_TEAM;
            // const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
            //
            // if (SERVICE_ID && PUBLIC_KEY) {
            //     const templateParams = {
            //         name: `${formData.firstName} ${formData.lastName}`.trim() || 'Anonymous visitor',
            //         email: formData.email || 'Not provided',
            //         phone: normalizedPhone || 'Not provided',
            //         message: formData.message
            //     };
            //     // Only the team notification can go out for a submission with no email —
            //     // there's nowhere to send the visitor's own confirmation copy.
            //     if (formData.email && TEMPLATE_USER) {
            //         await emailjs.send(SERVICE_ID, TEMPLATE_USER, templateParams, PUBLIC_KEY);
            //     }
            //     if (TEMPLATE_TEAM) {
            //         await emailjs.send(SERVICE_ID, TEMPLATE_TEAM, templateParams, PUBLIC_KEY);
            //     }
            // }

            setStatus({
                submitting: false,
                success: true,
                error: null,
                supportRequestNumber: result.support_request_number || null,
                confirmationEmail: result.confirmation_email || null
            });
            // Email/phone that came from the visitor's account weren't typed here — keep them
            // filled for "send another message" rather than making them find them again.
            setFormData((prev) => ({ firstName: '', lastName: '', email: emailFromAccount ? prev.email : '', message: '' }));
            if (!phoneFromAccount) {
                setPhone('');
                setPhoneCountry({ dialCode: '1', countryCode: 'us' });
            }
            setPhoneError('');

        } catch (err) {
            console.error('Submission error:', err);
            setStatus({
                submitting: false,
                success: false,
                error: err.message || 'Something went wrong. Please try again later.'
            });
        }
    };

    return (
        <main className="bg-[#F9F9F9] min-h-screen">
            <style>{`
                /* ── Contact phone container ── */
                .contact-phone-container {
                    width: 100% !important;
                }

                .contact-phone-container .form-control {
                    width: 100% !important;
                    height: 54px !important;
                    padding-left: 70px !important;
                    padding-right: 18px !important;
                    font-size: 14px !important;
                    font-weight: 700 !important;
                    color: #414141 !important;
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    outline: none !important;
                    font-family: inherit !important;
                    letter-spacing: 0.01em !important;
                    border-radius: 9999px !important;
                }

                .contact-phone-container .form-control::placeholder {
                    color: #a2a2a2 !important;
                    font-weight: 500 !important;
                }

                /* Flag button area */
                .contact-phone-container .flag-dropdown {
                    background: transparent !important;
                    border: none !important;
                    border-right: 1px solid rgba(127, 127, 127, 0.2) !important;
                    border-radius: 9999px 0 0 9999px !important;
                    width: 58px !important;
                    transition: background 0.15s ease !important;
                }

                .contact-phone-container .flag-dropdown:hover,
                .contact-phone-container .flag-dropdown.open {
                    background: rgba(0, 0, 0, 0.03) !important;
                    border-radius: 9999px 0 0 9999px !important;
                }

                .contact-phone-container .selected-flag {
                    width: 58px !important;
                    padding-left: 10px !important;
                    background: transparent !important;
                    border-radius: 9999px 0 0 9999px !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 6px !important;
                }

                .contact-phone-container .selected-flag .flag {
                    position: static !important;
                    transform: scale(1.15) !important;
                    margin: 0 !important;
                    flex-shrink: 0 !important;
                }

                .contact-phone-container .selected-flag .arrow {
                    border-top-color: #414141 !important;
                    margin-left: 4px !important;
                    flex-shrink: 0 !important;
                }

                .contact-phone-container .selected-flag .arrow.up {
                    border-bottom-color: #414141 !important;
                    border-top-color: transparent !important;
                }

                /* ── Dropdown ── */
                .contact-phone-dropdown {
                    border-radius: 20px !important;
                    border: 1px solid rgba(0, 0, 0, 0.08) !important;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12) !important;
                    padding: 8px !important;
                    margin-top: 8px !important;
                    background-color: #ffffff !important;
                    width: min(360px, calc(100vw - 48px)) !important;
                    max-height: 260px !important;
                    z-index: 9999 !important;
                    left: 0 !important;
                    overflow-x: hidden !important;
                }

                .contact-phone-dropdown::-webkit-scrollbar { width: 6px; }
                .contact-phone-dropdown::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 0, 0, 0.1);
                    border-radius: 10px;
                }

                .contact-phone-dropdown .country {
                    border-radius: 10px !important;
                    padding: 8px 12px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    color: #414141 !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 8px !important;
                    transition: background 0.12s ease !important;
                }

                .contact-phone-dropdown .country .flag { flex-shrink: 0 !important; }

                .contact-phone-dropdown .country .dial-code {
                    color: #6B6B6B !important;
                    font-weight: 800 !important;
                    min-width: 40px !important;
                }

                .contact-phone-dropdown .country .country-name {
                    color: #414141 !important;
                    font-weight: 600 !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                }

                .contact-phone-dropdown .country:hover { background-color: #f6f6f6 !important; }
                .contact-phone-dropdown .country.highlight { background-color: #ededed !important; }

                /* ── Search box inside dropdown ── */
                .contact-phone-dropdown .search {
                    position: sticky !important;
                    top: -8px !important;
                    z-index: 3 !important;
                    display: block !important;
                    padding: 10px !important;
                    margin: -8px -8px 6px !important;
                    background: #ffffff !important;
                    border-radius: 20px 20px 0 0 !important;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
                }

                .contact-phone-dropdown .search-emoji { display: none !important; }

                .contact-phone-dropdown .search-box {
                    box-sizing: border-box !important;
                    width: 100% !important;
                    height: 38px !important;
                    margin: 0 !important;
                    border: 1.5px solid #D9D9D9 !important;
                    border-radius: 10px !important;
                    padding: 0 12px !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #414141 !important;
                    outline: none !important;
                    font-family: inherit !important;
                    background: #F9F9F9 !important;
                    transition: border-color 0.2s, box-shadow 0.2s !important;
                }

                .contact-phone-dropdown .search-box:focus {
                    border-color: #D81F26 !important;
                    box-shadow: 0 0 0 3px rgba(216,31,38,0.18) !important;
                    background: #ffffff !important;
                }

                .contact-phone-dropdown .search-box::placeholder {
                    color: #a2a2a2 !important;
                    font-weight: 500 !important;
                }
            `}</style>

            <section className="px-6 py-8 md:px-8 md:py-10">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-10 items-start">
                    <div className="flex-1 w-full order-2 md:order-1">
                        <h1 className="text-2xl md:text-3xl font-black text-black mb-3 leading-[1.1] tracking-tighter">Contact us</h1>
                        <p className="text-base text-black/60 mb-0.5 leading-snug max-w-md font-medium">
                            Let us know what's on your mind. Whether it's a question, feedback, or a partnership inquiry, we're here to help.
                        </p>
                        <p className="text-base text-black mb-4 font-black tracking-tight">support@carfido.com</p>

                        {status.success ? (
                            <div className="bg-white/60 backdrop-blur-md border border-[#E6E6E6] p-10 rounded-[32px] mb-6 animate-in fade-in shadow-xl">
                                <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mb-6 shadow-lg">
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <h3 className="text-2xl font-black text-black mb-4">Message Sent!</h3>
                                {status.supportRequestNumber && (
                                    <p className="text-black font-black mb-2">Support request {status.supportRequestNumber}</p>
                                )}
                                <p className="text-black/60 font-medium mb-8 leading-relaxed">
                                    Your request is being handled. We'll get back to you soon.
                                    {status.confirmationEmail && ` We've emailed a confirmation to ${status.confirmationEmail}.`}
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={() => navigate(-1)}
                                        className="px-8 py-3 bg-white text-black border border-black/10 rounded-full text-xs font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all hover:bg-black/5"
                                    >
                                        Back to home
                                    </button>
                                    <button
                                        onClick={() => setStatus({ ...status, success: false })}
                                        className="px-8 py-3 bg-black text-white rounded-full text-xs font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-md"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form ref={formRef} className="space-y-5 bg-gradient-to-br from-white/90 to-[#F9F9F9]/80 backdrop-blur-xl p-6 md:p-8 rounded-[32px] border border-[#E6E6E6] shadow-lg" onSubmit={handleSubmit}>
                                <p className="text-xs text-black/40 font-medium -mt-1 mb-1">
                                    Only your question below is required — the rest is optional.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs text-[#414141] mb-1.5 font-bold ml-4">First name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            placeholder="John"
                                            className="w-full border border-[#D9D9D9] rounded-full px-5 py-3 text-sm bg-white/80 outline-none focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/10 transition-all font-bold text-[#414141] shadow-sm placeholder:text-[#a2a2a2]"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-[#414141] mb-1.5 font-bold ml-4">Last name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            placeholder="Doe"
                                            className="w-full border border-[#D9D9D9] rounded-full px-5 py-3 text-sm bg-white/80 outline-none focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/10 transition-all font-bold text-[#414141] shadow-sm placeholder:text-[#a2a2a2]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs text-[#414141] mb-1.5 font-bold ml-4">
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="john@example.com"
                                        className="w-full border rounded-full px-5 py-3 text-sm outline-none transition-all font-bold shadow-sm placeholder:text-[#a2a2a2] border-[#D9D9D9] bg-white/80 text-[#414141] focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/10"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs text-[#414141] mb-1.5 font-bold ml-4">
                                        Phone number
                                    </label>
                                    <div className={`relative rounded-full border shadow-sm transition-all overflow-visible bg-white/80 ${
                                        phoneError
                                            ? 'border-red-300 ring-4 ring-red-100'
                                            : 'border-[#D9D9D9] focus-within:border-[#D81F26] focus-within:ring-4 focus-within:ring-[#D81F26]/10 focus-within:bg-white'
                                    }`}>
                                        <PhoneInput
                                            key={phoneKey}
                                            country={'us'}
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            onBlur={handlePhoneBlur}
                                            placeholder="Phone number (optional)"
                                            enableSearch={true}
                                            searchPlaceholder="Search country or code…"
                                            disableSearchIcon={true}
                                            disableCountryGuess={true}
                                            disableInitialCountryGuess={false}
                                            countryCodeEditable={false}
                                            containerClass="contact-phone-container"
                                            dropdownClass="contact-phone-dropdown"
                                            inputProps={{
                                                name: 'phone',
                                                id: 'contact-phone-input',
                                            }}
                                        />
                                    </div>
                                    {phoneError && (
                                        <p className="mt-2 px-4 text-sm font-bold text-red-600">{phoneError}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs text-[#414141] mb-1.5 font-bold ml-4">How can we help?</label>
                                    <textarea
                                        rows={4}
                                        name="message"
                                        required
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Tell us more about your inquiry..."
                                        className="w-full border border-[#D9D9D9] rounded-[28px] px-5 py-4 text-sm bg-white/80 outline-none focus:border-[#D81F26] focus:bg-white focus:ring-4 focus:ring-[#D81F26]/10 resize-none transition-all font-bold text-[#414141] shadow-sm placeholder:text-[#a2a2a2]"
                                    />
                                </div>

                                {status.error && (
                                    <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm font-bold text-red-600 flex items-center gap-3">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                        {status.error}
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        disabled={status.submitting}
                                        onClick={() => navigate(-1)}
                                        className={`flex-1 flex items-center justify-center bg-white text-black font-black py-4 px-12 rounded-full uppercase tracking-[0.2em] text-xs border border-black/10 transition-all hover:scale-[1.02] active:scale-95 ${
                                            status.submitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-black/5'
                                        }`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={status.submitting}
                                        className={`flex-1 flex items-center justify-center bg-[#D81F26] hover:bg-[#B8181E] text-white font-black py-4 px-12 rounded-full uppercase tracking-[0.2em] text-xs transition-all hover:scale-[1.02] active:scale-95 shadow-[0_20px_40px_rgba(0,0,0,0.15)] ${
                                            status.submitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-[0_25px_50px_rgba(0,0,0,0.2)]'
                                        }`}
                                    >
                                        {status.submitting ? (
                                            <div className="flex items-center gap-3">
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Sending...
                                            </div>
                                        ) : 'Submit Inquiry'}
                                    </button>
                                </div>

                                {recaptchaOn && (
                                    <p className="text-[10px] text-center text-black/30 mt-4 font-bold">
                                        Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" className="underline hover:text-black">Privacy</a> & <a href="https://policies.google.com/terms" className="underline hover:text-black">Terms</a>.
                                    </p>
                                )}
                            </form>
                        )}
                    </div>

                    <div className="flex-none w-full md:w-[38%] sticky top-8 order-1 md:order-2">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-black/5 blur-[60px] rounded-full -z-10 group-hover:bg-black/10 transition-all duration-1000" />
                            <img
                                src="/images/Home_5.jpeg"
                                alt="Contact visual"
                                className="w-full h-auto rounded-[32px] object-cover shadow-xl border-4 border-white group-hover:scale-[1.02] transition-transform duration-700"
                                style={{ maxHeight: '70vh' }}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

// reCAPTCHA v3: the provider loads Google's script and supplies the context that
// useGoogleReCaptcha() reads inside ContactPageInner. It is only mounted when the server
// says reCAPTCHA is on — without a key, loading the script fails silently and leaves
// executeRecaptcha undefined forever, so "none" skips the provider. scriptProps keeps
// Google's script from blocking page render.
const ContactPage = () => {
    const [captcha, setCaptcha] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), CAPTCHA_CONFIG_TIMEOUT_MS);
        (async () => {
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/captcha-config`, { signal: controller.signal });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                if (data.mode === 'recaptcha' && data.site_key) {
                    setCaptcha({ mode: 'recaptcha', siteKey: data.site_key });
                } else if (data.mode === 'none') {
                    setCaptcha({ mode: 'none', siteKey: '' });
                } else {
                    throw new Error('Unrecognised captcha config');
                }
            } catch (err) {
                if (cancelled) return;
                console.warn('captcha-config unavailable, using build-time setting:', err.message);
                setCaptcha(BUILD_TIME_CAPTCHA);
            } finally {
                clearTimeout(timer);
            }
        })();
        return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
    }, []);

    if (!captcha) return null;

    return captcha.mode === 'recaptcha' ? (
        <GoogleReCaptchaProvider
            reCaptchaKey={captcha.siteKey}
            scriptProps={{ async: true, defer: true, appendTo: 'head' }}
        >
            <ContactPageInner captcha={captcha} />
        </GoogleReCaptchaProvider>
    ) : (
        <ContactPageInner captcha={captcha} />
    );
};

export default ContactPage;
